import { forwardRef, useMemo } from "react";
import { PixelRatio, StyleSheet, Text } from "react-native";
import { useTranslation } from "react-i18next";
import ViewShot, { type ViewShotRef } from "react-native-view-shot";
import type { TimeRange } from "@heraion/shared";

import { OffscreenCapture } from "../Logger/OffscreenCapture";
import { helpTextStyle } from "../primitives/helpTextStyle";
import { scaleTheme } from "../pictureScale";
import type { UiTheme } from "../theme";
import { ProgressReportBody } from "./ProgressReportBody";
import type { MaxHistory } from "./ProgressPrTimelineCard";
import { MAX_REPORT_PAGES, type ReportPageRun } from "./reportPagePlan";
import type { ProgressDeps } from "./types";

type FormatDate = (iso: string, locale: string) => string;

// 4:5 at 1080, what Instagram and the chats keep without squashing it.
const PAGE_WIDTH = 1080;
const PAGE_HEIGHT = 1350;
// Laid out at its real 1080 px, sized like a 400 pt phone screen: every
// token, chart and stroke grows by the same factor.
const PAGE_SCALE = PAGE_WIDTH / 400;
// No text on the picture under this, whatever its token (xs is 11 pt).
const PAGE_TEXT_FLOOR = 32;

// PG-64, inbox 978a9aae: one page of the shared report, drawn offscreen for
// the capture. `useReportPages` mounts one at a time.
export const ReportPageCapture = forwardRef<
  ViewShotRef,
  {
    deps: ProgressDeps & { range: TimeRange };
    run: ReportPageRun;
    formatDate: FormatDate;
    locale: string;
    maxHistory: MaxHistory;
    onLayout: () => void;
  }
>(function ReportPageCapture({ deps, run, formatDate, locale, maxHistory, onLayout }, ref) {
  const { t } = useTranslation();
  // The system text size is his screen's, not the picture's (always 1 on
  // web).
  const theme = useMemo(
    () =>
      scaleTheme(deps.theme, PAGE_SCALE, {
        textFloor: PAGE_TEXT_FLOOR,
        fontScale: PixelRatio.getFontScale(),
        strongBorders: true,
      }),
    [deps.theme],
  );
  const pageDeps = useMemo(() => ({ ...deps, theme }), [deps, theme]);
  const order = useMemo(() => [run.plan.id], [run.plan.id]);
  const styles = makeStyles(theme);
  return (
    <OffscreenCapture>
      <ViewShot
        ref={ref}
        options={{ format: "png", quality: 1, width: PAGE_WIDTH, height: PAGE_HEIGHT }}
        style={styles.page}
        onLayout={onLayout}
      >
        <ProgressReportBody
          deps={pageDeps}
          order={order}
          wide={false}
          formatDate={formatDate}
          locale={locale}
          maxHistory={maxHistory}
          branded
          page={run.page}
          slice={run.plan.slice}
        />
        {run.truncated ? (
          <Text style={styles.truncated}>
            {t("mobile.progress.report.pagesTruncated", {
              max: MAX_REPORT_PAGES,
              defaultValue:
                "The shared report stops at {{max}} images. Choose fewer sections to share the rest.",
            })}
          </Text>
        ) : null}
      </ViewShot>
    </OffscreenCapture>
  );
});

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    page: {
      width: PAGE_WIDTH,
      height: PAGE_HEIGHT,
      padding: s.s6,
      backgroundColor: p.bg,
      overflow: "hidden",
    },
    // Under the last card, in the room the page plan kept for it.
    truncated: { ...helpTextStyle(theme), marginTop: s.s4 },
  });
}
